import {
  Drawer,
  Box,
  Typography,
  Stack,
  LinearProgress,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../../api/apiFetch";
import CompaniesSection from "./CompaniesSection";
import { updateUserRole, toggleUserActive } from "../../api/users";
import { useAuth } from "../../context/AuthContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const API_URL = process.env.REACT_APP_API_URL;

type Props = {
  user: any | null;
  open: boolean;
  onClose: () => void;
  onUpdated: () => Promise<void>;
  showSuccess?: (msg: string) => void;
  showError?: (msg: string) => void;
};

export default function UserDetailsDrawer({
  user,
  open,
  onClose,
  onUpdated,
  showSuccess,
  showError,
}: Props) {
  const { t } = useAppTranslation("common");
  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState<any | null>(null);

  const loadData = useCallback(async () => {
    if (!user) return;

    setLoading(true);

    const res = await apiFetch(`${API_URL}/users/${user.id}`);
    const data = await res.json();

    setUserDetails({
      ...data,
      companies: data.companies || [],
    });

    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  if (!user) return null;

  const isSelf = currentUser?.id === user?.id;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 480,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f5f7fb",
        }}
      >
        {loading && <LinearProgress />}

        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h6" fontWeight={600} color="#111827">
            {user.display_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        </Box>

        {/* CONTENT */}
        <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
          <Stack spacing={2.5}>
            
            {/* 🔹 USER SECTION */}
            <Box
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 2,
                p: 2,
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ color: "#1f2937" }}
              >
                {t("Użytkownik")}
              </Typography>

              <Stack spacing={2} sx={{ mt: 2 }}>
                {/* ROLA */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontWeight: 500 }}
                  >
                    {t("Rola")}
                  </Typography>

                  <Select
                    fullWidth
                    size="small"
                    value={userDetails?.role || ""}
                    disabled={isSelf}
                    onChange={async (e) => {
                      try {
                        await updateUserRole(user.id, e.target.value);
                        await onUpdated();
                        showSuccess?.(t("Zmieniono rolę użytkownika"));
                      } catch {
                        showError?.(t("Błąd zmiany roli"));
                      }
                    }}
                    sx={{ mt: 0.5 }}
                  >
                    <MenuItem value="admin">{t("Admin")}</MenuItem>
                    <MenuItem value="accountant">{t("Księgowy")}</MenuItem>
                    <MenuItem value="external">{t("Zewnętrzny")}</MenuItem>
                  </Select>
                </Box>

                {/* AKTYWNOŚĆ */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" sx={{ color: "#111827" }}>
                    {t("Konto aktywne")}
                  </Typography>

                  <Switch
                    checked={userDetails?.is_active}
                    disabled={isSelf}
                    onChange={async (e) => {
                      try {
                        await toggleUserActive(user.id, e.target.checked);
                        await onUpdated();
                        showSuccess?.(t("Zmieniono status konta"));
                      } catch {
                        showError?.(t("Błąd zmiany statusu konta"));
                      }
                    }}
                  />
                </Stack>
              </Stack>
            </Box>

            {/* 🔹 COMPANIES */}
            <CompaniesSection
              user={user}
              data={userDetails}
              onUpdated={async () => {
                await loadData();
                await onUpdated();
              }}
            />
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}